import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { client } from './amplifyClient';
import { resolveExistingAppUserId } from './ensureAppUser';

export const MAX_PARENT_KIDS = 2;

export const PARENT_KIDS_CHANGED_EVENT = 'tw-parent-kids-changed';

export function notifyParentKidsChanged() {
  window.dispatchEvent(new Event(PARENT_KIDS_CHANGED_EVENT));
}

export type ParentKidProfileRow = {
  id: string;
  name?: string | null;
  parentId?: string | null;
  age?: number | null;
  dob?: string | null;
  /** Used for profile photo key in notes JSON */
  notes?: string | null;
};

/** @deprecated Use ParentKidProfileRow */
export type ParentKidProfileRef = ParentKidProfileRow;

/**
 * Parent kid lookups must use apiKey + every plausible parentId:
 * - Cognito sub (header / left-nav historically used this)
 * - App User.id (wizard may have stored email-linked User.id when it differs from Cognito)
 */
export async function resolveParentIdentityIds(): Promise<{
  cognitoUserId: string | null;
  appUserId: string | null;
  candidateParentIds: string[];
}> {
  let cognitoUserId: string | null = null;
  let email = '';
  try {
    await fetchAuthSession();
    const cognitoUser = await getCurrentUser();
    cognitoUserId = cognitoUser?.userId ?? null;
    const session = await fetchAuthSession();
    const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
    email = String(idPayload.email || '')
      .toLowerCase()
      .trim();
  } catch {
    return { cognitoUserId: null, appUserId: null, candidateParentIds: [] };
  }

  let appUserId: string | null = null;
  if (cognitoUserId) {
    try {
      appUserId = await resolveExistingAppUserId(cognitoUserId, email);
    } catch {
      appUserId = null;
    }
  }

  const candidateParentIds = Array.from(
    new Set([cognitoUserId, appUserId].filter((id): id is string => Boolean(id))),
  );

  return { cognitoUserId, appUserId, candidateParentIds };
}

export async function listKidProfilesForParentSession(): Promise<ParentKidProfileRow[]> {
  const { candidateParentIds } = await resolveParentIdentityIds();
  if (candidateParentIds.length === 0) return [];

  const byId = new Map<string, ParentKidProfileRow>();
  for (const parentId of candidateParentIds) {
    try {
      let nextToken: string | null | undefined = undefined;
      do {
        const res = await client.models.KidProfile.list({
          filter: { parentId: { eq: parentId } },
          limit: 100,
          nextToken: nextToken || undefined,
          authMode: 'apiKey',
        });
        for (const kid of res.data ?? []) {
          if (kid?.id) {
            byId.set(kid.id, {
              id: kid.id,
              name: kid.name,
              parentId: kid.parentId,
              age: kid.age,
              dob: kid.dob,
              notes: kid.notes,
            });
          }
        }
        nextToken = res.nextToken ?? null;
      } while (nextToken);
    } catch (e) {
      console.warn('listKidProfilesForParentSession failed for parentId', parentId, e);
    }
  }
  return [...byId.values()];
}

/** Canonical parentId for new KidProfile rows owned by a logged-in parent. */
export async function resolveCanonicalParentIdForCreate(
  preferredAppUserId?: string | null,
): Promise<string | null> {
  const { cognitoUserId, appUserId, candidateParentIds } = await resolveParentIdentityIds();
  // Prefer Cognito sub so header/left-nav (and owner auth) stay aligned.
  return cognitoUserId || preferredAppUserId || appUserId || candidateParentIds[0] || null;
}
